import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {Modal,ModalHeader,ModalBody,Row,Col,Button, FormGroup, Label, Input, InputGroup, Table
} from "reactstrap";
import { useDispatch } from "react-redux";
import { addRunbookScript, editRunbookScript } from "../../store/daemon/actions";
import { showNotification } from "components/Common/notification";

const AddRunbookScriptModal = ({ isOpen, toggle, scriptDetails, isViewMode = false }) => {
  const dispatch = useDispatch();
  const [scriptContent, setScriptContent] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [roleArn, setRoleArn] = useState("");
  const [parameters, setParameters] = useState([]);
  const [touched, setTouched] = useState({
    name: false,
    parameters: []
  });
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalValues, setOriginalValues] = useState(null);

  useEffect(() => {
    if (isOpen) {
      if (isViewMode && scriptDetails) {
        const scriptData = {
          name: scriptDetails.name || "",
          description: scriptDetails.description || "",
          roleArn: scriptDetails.role_arn || "",
          scriptContent: scriptDetails.script_content || "",
          parameters: []
        };
        if (scriptDetails.configurations) {
          scriptData.parameters = Object.entries(scriptDetails.configurations).map(([key, value]) => ({
            key,
            value: value.toString()
          }));
        }
        setName(scriptData.name);
        setDescription(scriptData.description);
        setRoleArn(scriptData.roleArn);
        setScriptContent(scriptData.scriptContent);
        setParameters(scriptData.parameters);
        setOriginalValues(scriptData);
        setIsEditing(false);
        setHasChanges(false);
      } else {
        resetForm();
      }
    }
  }, [isOpen, isViewMode, scriptDetails]);

  useEffect(() => {
    if (originalValues && isViewMode) {
      const currentValues = {
        name,
        description,
        roleArn,
        scriptContent,
        parameters
      };
      const hasNameChanged = currentValues.name !== originalValues.name;
      const hasDescriptionChanged = currentValues.description !== originalValues.description;
      const hasRoleArnChanged = currentValues.roleArn !== originalValues.roleArn;
      const hasScriptContentChanged = currentValues.scriptContent !== originalValues.scriptContent;
      let hasParametersChanged = false;
      if (currentValues.parameters.length !== originalValues.parameters.length) {
        hasParametersChanged = true;
      } else {
        for (let i = 0; i < currentValues.parameters.length; i++) {
          if (
            currentValues.parameters[i].key !== originalValues.parameters[i].key ||
            currentValues.parameters[i].value !== originalValues.parameters[i].value
          ) {
            hasParametersChanged = true;
            break;
          }
        }
      }
      setHasChanges(
        hasNameChanged ||
        hasDescriptionChanged ||
        hasRoleArnChanged ||
        hasScriptContentChanged ||
        hasParametersChanged
      );
    }
  }, [name, description, roleArn, scriptContent, parameters, originalValues, isViewMode]);

  function resetForm() {
    setScriptContent("");
    setName("");
    setDescription("");
    setRoleArn("");
    setParameters([]);
    setTouched({
      name: false,
      parameters: []
    });
    setIsEditing(false);
    setHasChanges(false);
    setOriginalValues(null);
  }

  function handleToggle() {
    resetForm();
    toggle();
  }

  function toggleEditMode() {
    setIsEditing(!isEditing);
  }

  const validateField = (value) => {
    return value.trim() === '';
  };

  const handleParameterChange = (index, field, value) => {
    const updatedParameters = parameters.map((param, i) =>
      i === index ? { ...param, [field]: value } : param
    );
    setParameters(updatedParameters);
  };

  const handleAddRequestParameter = () => {
    setParameters([...parameters, { key: '', value: '' }]);
    setTouched(prevTouched => ({
      ...prevTouched,
      parameters: [
        ...(prevTouched.parameters || []),
        { key: false, value: false }
      ],
    }));
  };

  const handleDeleteRequestParameter = (index) => {
    const updatedParameters = parameters.filter((_, i) => i !== index);
    const updatedTouched = touched.parameters.filter((_, i) => i !== index);
    setParameters(updatedParameters);
    setTouched({ ...touched, parameters: updatedTouched });
  };

  const handleBlur = (index, field, type) => {
    if (type === 'parameters') {
      const updatedTouched = [...touched.parameters];
      updatedTouched[index] = {
        ...updatedTouched[index],
        [field]: true
      };
      setTouched({ ...touched, parameters: updatedTouched });
    } else {
      setTouched({ ...touched, [field]: true });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!scriptContent.trim()) {
      showNotification("error", "Script content cannot be empty");
      return;
    }
    if (!name.trim()) {
      showNotification("error", "Script name is required");
      return;
    }
    const invalidParams = parameters.some(param => !param.key.trim() || !param.value.trim());
    if (invalidParams) {
      showNotification("error", "All parameter keys and values must be filled");
      return;
    }
    const configurations = {};
    parameters.forEach(param => {
      configurations[param.key] = param.value;
    });

    const scriptData = {
      script_content: scriptContent,
      name: name,
      description: description,
      role_arn: roleArn,
      configurations: configurations
    };
    if (isViewMode && scriptDetails) {
      scriptData.id = scriptDetails.id;
      dispatch(editRunbookScript(scriptData));
    } else {
      dispatch(addRunbookScript(scriptData));
    }

    handleToggle();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleToggle}
      role="dialog"
      autoFocus={true}
      centered
      backdrop="static"
      size="lg"
      className="exampleModal"
      tabIndex="-1"
    >
      <div className="modal-content">
        <ModalHeader toggle={handleToggle} className="border-bottom-2">
          {isViewMode ?
            (isEditing ? `Edit Runbook Script: ${scriptDetails?.name}` : `Runbook Script: ${scriptDetails?.name}`)
            : "Add Runbook Script"}
        </ModalHeader>
        <ModalBody>
          <div className="d-flex flex-column">
            <Row>
              <Col lg="12" className="mb-3">
                <FormGroup>
                  <Label for="scriptName">Script Name <span className="text-danger">*</span></Label>
                  <Input
                    className="input no-background"
                    type="text"
                    id="scriptName"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onBlur={() => setTouched({ ...touched, name: true })}
                    invalid={!isViewMode && touched.name && validateField(name)}
                    placeholder="Enter script name"
                    disabled={isViewMode && !isEditing}
                  />
                  {!isViewMode && touched.name && validateField(name) && (
                    <div className="text-danger mt-1">Script name is required</div>
                  )}
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col lg="12" className="mb-3">
                <FormGroup>
                  <Label for="scriptDescription">Description</Label>
                  <Input
                    className="input no-background"
                    type="textarea"
                    id="scriptDescription"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Enter script description"
                    rows={3}
                    disabled={isViewMode && !isEditing}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col lg="12" className="mb-3">
                <FormGroup>
                  <Label for="roleArn">Lambda Role ARN (Role ARN for the Lambda function)</Label>
                  <Input
                    className="input no-background"
                    type="text"
                    id="roleArn"
                    value={roleArn}
                    onChange={(e) => setRoleArn(e.target.value)}
                    placeholder="Enter AWS Role ARN"
                    disabled={isViewMode && !isEditing}
                  />
                </FormGroup>
              </Col>
            </Row>
            {isViewMode && scriptDetails?.lambda_arn && (
              <Row>
                <Col lg="12" className="mb-3">
                  <FormGroup>
                    <Label for="lambdaArn">Lambda ARN</Label>
                    <Input
                      type="text"
                      id="lambdaArn"
                      value={scriptDetails.lambda_arn}
                      disabled={true}
                    />
                  </FormGroup>
                </Col>
              </Row>
            )}
            <Row>
              <Col lg="12" className="mb-3">
                <Label>Script Content <span className="text-danger">*</span></Label>
                <div style={{ position: "relative", borderRadius: "4px", overflow: "hidden" }}>
                                    <textarea
                                      value={scriptContent}
                                      onChange={(e) => setScriptContent(e.target.value)}
                                      rows={10}
                                      placeholder="# Enter your script here"
                                      style={{
                                        fontFamily: "monospace",
                                        fontSize: "14px",
                                        width: "100%",
                                        backgroundColor: "#292036",
                                        color: "white",
                                        border: "1px solid rgba(255, 255, 255, 0.2)",
                                        padding: "10px",
                                        whiteSpace: "pre",
                                        overflowX: "auto",
                                        overflowY: "auto",
                                        resize: "vertical",
                                        minHeight: "200px",
                                        tabSize: "4"
                                      }}
                                      disabled={isViewMode && !isEditing}
                                    />
                </div>
                {!isViewMode && (
                  <small className="form-text text-white mt-2">
                    Script indentation will be preserved when uploaded.
                  </small>
                )}
              </Col>
            </Row>
            <Row className="mt-3">
              <Col lg="12">
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <Label>Configuration Parameters</Label>
                  {(!isViewMode || (isViewMode && isEditing)) && (
                    <i className="mdi mdi-plus-circle"
                       onClick={handleAddRequestParameter}
                       style={{marginLeft: '10px', fontSize: '24px', color: '#3569f2', marginBottom: '0.5rem'}}
                    />
                  )}
                </div>
                {parameters.length > 0 ? (
                  (isViewMode && !isEditing) ? (
                    <Table bordered responsive className="mt-2">
                      <thead>
                      <tr>
                        <th style={{ width: "40%" }}>Key</th>
                        <th style={{ width: "60%" }}>Value</th>
                      </tr>
                      </thead>
                      <tbody>
                      {parameters.map((param, idx) => (
                        <tr key={idx}>
                          <td>{param.key}</td>
                          <td>{param.value}</td>
                        </tr>
                      ))}
                      </tbody>
                    </Table>
                  ) : (
                    parameters.map((param, index) => (
                      <Row key={index} className="mb-2">
                        <Col xs="5">
                          <Input
                            type="text"
                            value={param.key}
                            onChange={(e) => handleParameterChange(index, 'key', e.target.value)}
                            onBlur={() => handleBlur(index, 'key', 'parameters')}
                            invalid={touched.parameters[index]?.key && validateField(param.key)}
                            placeholder="Key"
                            disabled={isViewMode && !isEditing}
                          />
                          {touched.parameters[index]?.key && validateField(param.key) && (
                            <div className="text-danger">Key is required</div>
                          )}
                        </Col>

                        <Col xs="5">
                          <Input
                            type="text"
                            value={param.value}
                            onChange={(e) => handleParameterChange(index, 'value', e.target.value)}
                            onBlur={() => handleBlur(index, 'value', 'parameters')}
                            invalid={touched.parameters[index]?.value && validateField(param.value)}
                            placeholder="Value"
                            disabled={isViewMode && !isEditing}
                          />
                          {touched.parameters[index]?.value && validateField(param.value) && (
                            <div className="text-danger">Value is required</div>
                          )}
                        </Col>
                        <Col xs="2">
                          {(!isViewMode || (isViewMode && isEditing)) && (
                            <Button
                              color="danger"
                              size="sm"
                              onClick={() => handleDeleteRequestParameter(index)}
                              className="d-flex align-items-center"
                            >
                              <i className="mdi mdi-delete" />
                            </Button>
                          )}
                        </Col>
                      </Row>
                    ))
                  )
                ) : (
                  <p className="text-white">No configuration parameters defined.</p>
                )}
              </Col>
            </Row>
            <Row className="mt-4">
              <Col className="d-flex justify-content-end">
                {isViewMode ? (
                  isEditing ? (
                    <>
                      <Button
                        color="secondary"
                        className="me-2"
                        onClick={toggleEditMode}
                      >
                        Cancel
                      </Button>
                      <Button
                        color="primary"
                        onClick={handleSubmit}
                        disabled={!hasChanges}
                      >
                        Save Changes
                      </Button>
                    </>
                  ) : (
                    <Button
                      color="primary"
                      onClick={toggleEditMode}
                    >
                      Edit
                    </Button>
                  )
                ) : (
                  <Button
                    type="submit"
                    color="primary"
                    onClick={handleSubmit}
                  >
                    Submit
                  </Button>
                )}
              </Col>
            </Row>
          </div>
        </ModalBody>
      </div>
    </Modal>
  );
};

AddRunbookScriptModal.propTypes = {
  toggle: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  scriptDetails: PropTypes.object,
  isViewMode: PropTypes.bool
};

export default AddRunbookScriptModal;